import { useCallback, useEffect, useRef, useState } from 'react';

import { ISADMINLOGGED } from '../constants/global';
import { startActivityTracking, subscribeToActivity } from '../session/activity';

/**
 * Fires `onTimeout` after `timeoutDuration` ms without user activity.
 *
 * Activity comes from the shared store in `src/session/activity.ts` — the same one the
 * proactive session refresh reads — so "idle" means exactly one thing app-wide: a user
 * this hook is about to warn is never a user the refresh keeps alive, and vice versa.
 *
 * Armed on `ISADMINLOGGED()` rather than `ISSESSION()`: the old gate disarmed the idle
 * timer the moment the access token passed `exp`, which is precisely when an unattended
 * session most needs closing.
 */
export const useInactivityTimeout = (timeoutDuration: number, onTimeout: () => void, sessionTimeOut: boolean) => {
	const [isInactive, setIsInactive] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const onTimeoutRef = useRef(onTimeout);
	onTimeoutRef.current = onTimeout;

	const resetTimer = useCallback(() => {
		if (timer.current) clearTimeout(timer.current);
		timer.current = null;
		if (ISADMINLOGGED()) {
			timer.current = setTimeout(() => {
				setIsInactive(true);
				onTimeoutRef.current();
			}, timeoutDuration);
		}
		if (sessionTimeOut) {
			onTimeoutRef.current();
		}
	}, [timeoutDuration, sessionTimeOut]);

	useEffect(() => {
		const release = startActivityTracking();
		const unsubscribe = subscribeToActivity(() => {
			setIsInactive(false);
			resetTimer();
		});

		resetTimer();

		return () => {
			unsubscribe();
			release();
			if (timer.current) clearTimeout(timer.current);
			timer.current = null;
		};
	}, [resetTimer]);

	return isInactive;
};
